import Util from '../../../forecast-app/shared/util/util';
import UpdateApprovalStatusMutation from '../../../mutations/UpdateApprovalStatusMutation';
import {APPROVAL_TYPE, ESTIMATION_UNIT, profilePicSrc, APPROVAL_STATUS} from '../../../constants';
import {showModal, MODAL_TYPE} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {graphql} from 'react-relay';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {personIsVirtualUser} from '../../../forecast-app/shared/util/PermissionsUtil';

export const GROUP_BY = {
	PERSON: 'person',
	PROJECT: 'project',
};

const getToastMessage = (person, approvalType, intl) => {
	if (!person) return intl.formatMessage({id: 'approval.project_approved'});
	else if (approvalType === APPROVAL_TYPE.UNAPPROVE || approvalType === APPROVAL_TYPE.UNSUBMIT) {
		return intl.formatMessage({id: 'approval.person_timesheet_rejected'}, {person: person.firstName || person.lastName});
	} else if (approvalType === APPROVAL_TYPE.APPROVE) {
		return intl.formatMessage({id: 'approval.person_timesheet_approved'}, {person: person.firstName || person.lastName});
	}
};

export const UpdateApprovalStatus = (personIds, projectIds, approvalType, currentViewingDate, intl, person) => {
	const startDate = currentViewingDate.clone().startOf('week');
	const endDate = currentViewingDate.clone().endOf('week');
	const update = message => {
		const onSuccess = () => {
			const toastMessage = getToastMessage(person, approvalType, intl);
			if (toastMessage) {
				createToast({duration: 4000, message: toastMessage});
			}
		};
		const mutationData = {
			personIds: personIds,
			projectIds: projectIds,
			approvalType: approvalType,
			startYear: startDate.year(),
			startMonth: startDate.month() + 1,
			startDay: startDate.date(),
			endYear: endDate.year(),
			endMonth: endDate.month() + 1,
			endDay: endDate.date(),
			sendNotification: true,
		};
		if (message) {
			mutationData.rejectionMessage = message;
		}
		Util.CommitMutation(UpdateApprovalStatusMutation, mutationData, onSuccess);
	};

	// if only one person, we show modal for setting a message for the email, otherwise we just send the email
	if (!!personIds && personIds.length === 1 && approvalType === APPROVAL_TYPE.UNSUBMIT) {
		showModal({
			type: MODAL_TYPE.REJECT_TIMEAPPROVAL_SUBMISSION,
			startDate,
			endDate,
			rejectForName: person.fullName,
			onReject: update,
		});
	} else {
		update();
	}
};

export const getPersonData = (datas, parentId, otherPerson, allTimes) => {
	let res = [];

	datas &&
		datas.forEach(data => {
			const {id, firstName, lastName, fullName, profilePictureId} = data.person;
			const timeRegDate = Util.CreateNonUtcMomentDate(data.year, data.month, data.day);
			const dayIdx = timeRegDate.weekday();
			const idx = res.findIndex(r => r.person.id === id);
			if (idx > -1) {
				res[idx].person.timeRegs.push(data);
				res[idx].person.totals[dayIdx] += data.minutesRegistered;
				res[idx].person.total += data.minutesRegistered;
				res[idx].person.noApprovalStatusChange = res[idx].person.noApprovalStatusChange || data.noApprovalStatusChange;
			} else {
				const minutesRegisteredTotals = [null, null, null, null, null, null, null];
				minutesRegisteredTotals[dayIdx] = data.minutesRegistered;
				res.push({
					person: {
						id: id,
						firstName: firstName,
						lastName: lastName,
						fullName: fullName,
						profilePictureId: profilePictureId ? profilePicSrc(profilePictureId) : null,
						timeRegs: [data],
						totals: minutesRegisteredTotals,
						total: data.minutesRegistered,
						parentId: parentId,
						noApprovalStatusChange: data.noApprovalStatusChange,
					},
				});
			}
		});

	//add the people who dind't register any time yet
	if (otherPerson) {
		const res2 = [];
		otherPerson &&
			otherPerson
				.filter(person => !personIsVirtualUser(person.node))
				.forEach(person => {
					const idxNotSubmitted = res.findIndex(r => r.person.id === person.node.id);
					const idxApproved = allTimes[APPROVAL_STATUS.APPROVED].findIndex(r => r.person.id === person.node.id);
					const idxSubmitted = allTimes[APPROVAL_STATUS.SUBMITTED].findIndex(r => r.person.id === person.node.id);

					if (idxNotSubmitted === -1 && idxApproved === -1 && idxSubmitted === -1) {
						res2.push({
							person: {
								id: person.node.id,
								firstName: person.node.firstName,
								lastName: person.node.lastName,
								fullName: person.node.fullName,
								profilePictureId: person.node.profilePictureId ? profilePicSrc(person.node.profilePictureId) : null,
								timeRegs: [],
								totals: [],
								total: null,
								parentId: null,
								noApprovalStatusChange: null,
							},
						});
					}
				});
		res2.sort((a, b) => (a.person.firstName > b.person.firstName ? 1 : b.person.lastName > a.person.lastName ? -1 : 0));
		res = [...res, ...res2];
	}

	return res;
};

export const getProjectData = (datas, parentId) => {
	const res = [];

	datas &&
		datas.forEach(data => {
			const timeRegDate = Util.CreateNonUtcMomentDate(data.year, data.month, data.day);
			const dayIdx = timeRegDate.weekday();
			const project = data.task ? data.task.project : data.project;
			const projectIdx = res.findIndex(r => {
				return r.project ? r.project.id === project.id : r.task.project.id === project.id;
			});
			if (projectIdx > -1) {
				res[projectIdx].project.timeRegs.push(data);
				res[projectIdx].project.totals[dayIdx] += data.minutesRegistered;
				res[projectIdx].project.total += data.minutesRegistered;
				res[projectIdx].project.noApprovalStatusChange = res[projectIdx].project.noApprovalStatusChange || data.noApprovalStatusChange;
			} else {
				const timeRegsTotals = [null, null, null, null, null, null, null];
				timeRegsTotals[dayIdx] = data.minutesRegistered;
				const projectOwner = project.projectPersons.edges.map(projectOwner => {
					const person = projectOwner.node.person;
					return {
						fullName: person.fullName,
						imageSource: person.profilePictureId ? profilePicSrc(person.profilePictureId) : null,
					};
				});

				res.push({
					project: {
						id: project.id,
						companyProjectId: project.companyProjectId,
						name: project.name,
						projectColor: project.projectColor,
						timeRegs: [data],
						totals: timeRegsTotals,
						total: data.minutesRegistered,
						projectOwner: projectOwner,
						parentId: parentId,
						noApprovalStatusChange: data.noApprovalStatusChange,
						/* projectOwnerId: projectOwner && projectOwner.id,
						projectOwnerProfilePictureId: projectOwner && projectOwner.profilePictureId,
						projectOwnerFullName: projectOwner && projectOwner.fullName, */
					},
				});
			}
		});
	return res;
};

export const getTimeData = datas => {
	const res = [];

	datas &&
		datas.forEach(data => {
			const timeRegDate = Util.CreateNonUtcMomentDate(data.year, data.month, data.day);
			const dayIdx = timeRegDate.weekday();
			const taskId = data.task ? data.task.id : 'project_time';
			const taskIdx = res.findIndex(r => {
				return r.task.id === taskId;
			});

			if (taskIdx > -1) {
				res[taskIdx].task.totals[dayIdx] += data.minutesRegistered;
				res[taskIdx].task.total += data.minutesRegistered;
				data.notes && res[taskIdx].task.timeRegistrationsWithNotes.push(data);
			} else {
				const timeRegsTotals = [null, null, null, null, null, null, null];
				timeRegsTotals[dayIdx] = data.minutesRegistered;

				if (data.task) {
					//only used for the estimation so on task level
					const storyPointConversion = data.task && data.task.project.estimationUnit === ESTIMATION_UNIT.POINTS ? data.task.project.minutesPerEstimationPoint : 1;
					res.push({
						task: {
							id: taskId,
							taskId: data.task.companyTaskId,
							name: data.task.name,
							timeRegistrationsWithNotes: data.notes ? [data] : [],
							totals: timeRegsTotals,
							total: data.minutesRegistered,
							estimated: data.task.estimateForecast * storyPointConversion,
							totalTaskTimeRegs: data.task.totalMinutesRegistered,
						},
					});
				} else {
					//TimeRegs on project. Kkeep the object as Task, to avoid more if
					res.push({
						task: {
							id: taskId,
							timeRegistrationsWithNotes: data.notes ? [data] : [],
							totals: timeRegsTotals,
							total: data.minutesRegistered,
						},
					});
				}
			}
		});
	return res;
};

export const getNoApprovalStatusChangeMessage = (noApprovalStatusChange, formatMessage) => {
	const noApprovalChangeGenericMessage = formatMessage({id: 'approval.no_approval_status_change'});
	switch (noApprovalStatusChange) {
		case 'INVOICED':
			return noApprovalChangeGenericMessage + formatMessage({id: 'approval.no_approval_status_change_invoice'});
		case 'LOCKED_PERIOD':
			return noApprovalChangeGenericMessage + formatMessage({id: 'approval.no_approval_status_change_locked_period'});
		case 'COMPANY_LOCK':
			return noApprovalChangeGenericMessage + formatMessage({id: 'approval.no_approval_status_change_company_lock'});
		default:
			return null;
	}
};

export const ownedProjectPersonsQuery = graphql`
	query TimeApprovalLogicQuery($personId: ID) {
		viewer {
			id
			actualPersonId
			company {
				id
				person(id: $personId) {
					id
					ownedProjectPersons(first: 1000000) {
						edges {
							node {
								id
								firstName
								lastName
								profilePictureId
								permissions
							}
						}
					}
				}
			}
		}
	}
`;

/* export const ownedProjectPersonsQuery = graphql`
	fragment TimeApprovalLogic_viewer on Viewer @argumentDefinitions(personId: {type: "ID"}) {
		id
		actualPersonId
		company {
			id
			person(id: $personId) {
				id
				ownedProjectPersons(first: 1000) {
					edges {
						node {
							id
							firstName
							lastName
							profilePictureId
						}
					}
				}
			}
		}
	}
`;
 */
